import State from 'store/State';
import { PortfolioSelectionWrapper } from 'portfolio/PortfolioSelectionWrapper';
import {
    VenueAnalysisByOrderIdsOrSymbolStreamRequest,
    VenueAnalysisByOrderIdsStreamRequest,
    VenueAnalysisBySymbolStreamRequest,
} from 'widgets/venueAnalysis/api/VenueAnalysisByOrderIdsOrSymbolStreamRequest';
import { toOrderIdsStreamRequest } from 'widgets/streaming/OrderIdsStreamRequest';
import { StreamRequestInfo } from 'widgets/streaming/widgetStreamManager/WidgetCustomStreamManager';
import { WidgetConfigWithSymbol } from 'widgets/WidgetSettingsEditing/WidgetSettingsSymbolEditor';
import {
    selectPortfolioSelection,
    selectPortfolioStreamingOrderIds,
} from 'portfolio/store/selectors';
import { selectAppSettings } from 'app/store/appSettings/selectors';
import { WidgetId } from 'widgets/WidgetId';
import { selectConfiguredSymbolForWidgetOrItsCapsuleContainer } from 'widgets/tiles/store/selectors';

const buildStreamRequest: (
    state: State,
    id: WidgetId,
    activeInputUsesOrdersStream: boolean
) => VenueAnalysisByOrderIdsOrSymbolStreamRequest | undefined = (
    state,
    id,
    activeInputUsesOrdersStream
) => {
    // Configured symbol wins...
    const configuredSymbol =
        selectConfiguredSymbolForWidgetOrItsCapsuleContainer(state, id);
    const portfolioSelection = selectPortfolioSelection(state);

    if (configuredSymbol) {
        return {
            symbol: configuredSymbol,
            symbology: selectAppSettings(state).symbology,
        };
    }

    // If the selection has orders eg from Outliers, we use those...
    if ((portfolioSelection?.selectedOrders ?? []).length > 0) {
        return toOrderIdsStreamRequest(
            portfolioSelection!.selectedOrders.map((s) => s.orderID)
        );
    }

    // Next try the streaming order ids, if the active input uses the orders stream...
    if (activeInputUsesOrdersStream) {
        const orderIds = selectPortfolioStreamingOrderIds(state);

        if (orderIds && orderIds.length > 0) {
            return toOrderIdsStreamRequest(orderIds);
        }
    }

    // Finally the selected symbol...
    const { selectedSymbol, symbology } = new PortfolioSelectionWrapper(
        portfolioSelection
    );

    if (selectedSymbol) {
        return {
            symbol: selectedSymbol,
            symbology,
        };
    }
};

const requestToStreamKey: (
    request: VenueAnalysisByOrderIdsOrSymbolStreamRequest
) => string | undefined = (request) => {
    const { symbol } = request as VenueAnalysisBySymbolStreamRequest;
    const { orderIds } = request as VenueAnalysisByOrderIdsStreamRequest;
    return toStreamKey(symbol, orderIds);
};

function toRequestInfo(
    payload: VenueAnalysisByOrderIdsOrSymbolStreamRequest
): StreamRequestInfo<VenueAnalysisByOrderIdsOrSymbolStreamRequest> {
    return {
        streamKey: requestToStreamKey(payload)!,
        // We have one stream for orderIds so we need to set the payloadUid...
        payloadUid: (payload as VenueAnalysisByOrderIdsStreamRequest).orderIds,
        payload,
    };
}

export const OrderIdsStreamKey = 'venueAnalysis-orderIds';

function toStreamKey(
    symbol: string | undefined,
    orderIds: string | undefined
): string | undefined {
    if (symbol) {
        // A stream per symbol...
        return `venueAnalysis-symbol-${symbol}`;
    }
    if (orderIds) {
        // We use one stream key (and therefore one channel) for all order id selections,
        // as only one set of order ids data can be visible in the app at any one time
        // i.e. capsules can be pinned to symbols, but not orders
        return OrderIdsStreamKey;
    }
}

const getWidgetStreamRequestInfo: (
    state: State,
    id: WidgetId,
    activeInputUsesOrdersStream: boolean
) =>
    | StreamRequestInfo<VenueAnalysisByOrderIdsOrSymbolStreamRequest>
    | undefined = (state, id, activeInputUsesOrdersStream) => {
    const request = buildStreamRequest(state, id, activeInputUsesOrdersStream);
    return request && toRequestInfo(request);
};

const getStreamRequestInfosForSymbols: (
    state: State,
    symbols: string[]
) => StreamRequestInfo<VenueAnalysisByOrderIdsOrSymbolStreamRequest>[] = (
    state,
    symbols
) => {
    const { symbology } = selectAppSettings(state);
    return symbols.map((symbol) => {
        const payload = { symbol, symbology };
        const streamKey = requestToStreamKey(payload)!;
        return { streamKey, payload };
    });
};

const createWidgetRequestUid: (
    state: State,
    id: WidgetId,
    activeInputUsesOrdersStream: boolean
) => string | undefined = (state, id, activeInputUsesOrdersStream) => {
    const request = buildStreamRequest(state, id, activeInputUsesOrdersStream);
    if (!request) {
        return undefined;
    }
    return requestToStreamKey(request);
};

const toRequestUidForConfigOrSymbol: (
    symbol: string,
    config: WidgetConfigWithSymbol | undefined
) => string = (symbol, config) => {
    return toStreamKey(config?.symbol ? config?.symbol : symbol, undefined)!;
};

export {
    getWidgetStreamRequestInfo,
    getStreamRequestInfosForSymbols,
    createWidgetRequestUid,
    toRequestUidForConfigOrSymbol,
};
